import { Avatar } from '@app/components/core/avatar/Avatar'
import { Button } from '@app/components/core/button/Button'
import { Title } from '@app/components/core/typography/Title'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { i18n } from '@i18n/index'
import { pluralize } from '@i18n/localizeHelper'
import { useLanguage } from '@i18n/useLanguage'
import { DataCard } from './DataCard'
import { useCallback, useMemo, useState } from 'react'
import { Modal } from '@app/components/blocks/modal/Modal'
import { AvatarStack } from '@app/components/blocks/avatarStack/AvatarStack'
import { ViewPlotModel } from 'functions/app/plot/view'
import { fullName } from '@app/util/helpers'
import { UserList } from '@app/components/blocks/userList/UserList'
import { UserInitiativeCard } from '@app/components/blocks/card/UserInitiativeCard'
import { faArrowRight, faSend } from '@fortawesome/pro-regular-svg-icons'
import { useAuthState } from '@based/sdk'
import { isUserAdmin } from '@schema/utils'
import { useMessageBox } from '@app/util/hooks/useMessageBox'

export const RightColumn = ({ plot }: { plot: ViewPlotModel }) => {
  const [language, _] = useLanguage()
  const translations = i18n.languages[language].app.plotPage
  const commonTranslations = i18n.languages[language].app.common
  const [followingUsersModal, setFollowingUsersModal] = useState(false)
  const [followingInitiativesModal, setFollowingInitiativesModal] =
    useState(false)
  const auth = useAuthState()
  const { showMessageBox } = useMessageBox()

  const ownerName = plot.owner?.name || 'Unknown'
  const ownerAvatar = plot.owner?.avatar?.src

  const followingInitiatives = plot.followingInitiatives ?? []

  const canMessageAll =
    auth.userId &&
    plot.followers.some(
      (f) =>
        f.id === Number(auth.userId) &&
        f.initiatives?.some((u) => isUserAdmin(u)),
    )

  const messageRecipients = useMemo(() => {
    return plot.followers
      .filter((f) =>
        f.initiatives?.some((u) => isUserAdmin(u)),
      )
      .map((f) => {
        const adminInitiative = f.initiatives?.find((u) => isUserAdmin(u))
        return {
          id: f.id,
          name: fullName(f),
          avatar: f.avatar,
          role: adminInitiative?.$role,
          initiativeId: adminInitiative?.id,
        }
      })
  }, [plot.followers])

  const onMessageClick = useCallback(
    (userId: number) => {
      const recipient = messageRecipients.find((r) => r.id === userId)
      if (!recipient?.initiativeId) return
      const filteredRecipients = messageRecipients
        .filter((r) => r.initiativeId === recipient.initiativeId)
        .map(({ id, name, avatar, role }) => ({
          id,
          name,
          avatar,
          role,
        }))
      showMessageBox(filteredRecipients, recipient.initiativeId, userId)
      setFollowingUsersModal(false)
    },
    [messageRecipients, showMessageBox],
  )

  return (
    <Column
      gap="2.5rem"
      alignItems="stretch"
      style={{ flex: '1 1 23rem', minWidth: '23rem' }}
    >
      <DataCard plot={plot} />
      {plot.owner && (
        <Column gap="1.5rem">
          <Title.H4>{translations.offeredBy}</Title.H4>
          <Row style={{ alignItems: 'center' }}>
            <Avatar size="medium" labelSource={ownerName} image={ownerAvatar} />
            <Title.H5>{ownerName}</Title.H5>
          </Row>
        </Column>
      )}
      {followingInitiatives.length > 0 && (
        <Column alignItems="stretch">
          <Row style={{ justifyContent: 'space-between' }}>
            <Title.H4>
              {pluralize(
                translations.initiative,
                followingInitiatives.length,
              )}
            </Title.H4>
            <Button
              variant="link"
              label={i18n.languages[language].app.common.seeMore}
              icon={faArrowRight}
              onClick={() => setFollowingInitiativesModal(true)}
            />
          </Row>
          {followingInitiatives.slice(0, 3).map((initiative) => (
            <UserInitiativeCard
              key={initiative.id}
              id={initiative.id}
              slug={initiative.slug}
              title={initiative.title}
              subTitle={initiative.developmentForm?.name}
              image={initiative.images?.[0]?.src}
            />
          ))}
        </Column>
      )}
      {plot.followers.length > 0 && (
        <Column>
          <Title.H4>
            {plot.followers.length > 0
              ? plot.followers.length + ' ' + translations.interestListText
              : translations.interestListText}
          </Title.H4>
          <AvatarStack
            size={'medium'}
            avatars={plot.followers}
            onClick={() => setFollowingUsersModal(true)}
          />
        </Column>
      )}
      <Modal
        title={translations.follower.plural}
        isOpen={followingUsersModal}
        onClose={() => setFollowingUsersModal(false)}
      >
        <UserList
          gap="1.5rem"
          users={plot.followers.map((u) => ({
            id: u.id,
            name: fullName(u),
            avatar: u.avatar,
            handle: u.handle,
            action: canMessageAll &&
              messageRecipients.some((r) => r.id === u.id) && (
                <Button
                  variant="secondary"
                  size="small"
                  icon={faSend}
                  label={commonTranslations.sendMessage}
                  iconPosition="left"
                  onClick={() => onMessageClick(u.id)}
                />
              ),
          }))}
          style={{
            minWidth: '30rem',
          }}
        />
      </Modal>
      <Modal
        title={translations.initiative.plural}
        isOpen={followingInitiativesModal}
        onClose={() => setFollowingInitiativesModal(false)}
      >
        <Column gap="0.5rem" alignItems="stretch" style={{ minWidth: '30rem' }}>
          {followingInitiatives.map((initiative) => (
            <UserInitiativeCard
              key={initiative.id}
              id={initiative.id}
              slug={initiative.slug}
              title={initiative.title}
              subTitle={initiative.developmentForm?.name}
              image={initiative.images?.[0]?.src}
            />
          ))}
        </Column>
      </Modal>
    </Column>
  )
}
